import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import {
  getCookieConsent,
  setCookieConsent,
  subscribeCookiePreferencesOpen,
} from '@/lib/cookieConsent';
import { LEGAL_DOCS } from '@/config/legal';

/**
 * Bottom-of-screen cookie consent banner (GDPR / ePrivacy).
 *
 * Shows on first visit (no stored choice) on every surface — management and
 * learner alike, since cookies are set for every visitor. Implements the three
 * options promised by the Cookies Policy §4: accept all, reject non-essential,
 * and a configure panel with per-category toggles. Analytics (PostHog) only
 * loads after acceptance — see the consent gate in `main.tsx`. The choice can
 * be revisited any time via the footer's "Cookie preferences" link, which
 * re-opens this banner through `openCookiePreferences()`.
 */
const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(() => getCookieConsent() === null);
  const [configuring, setConfiguring] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(
    () => getCookieConsent()?.analytics ?? true
  );

  // Footer "Cookie preferences" re-opens the banner straight into the panel.
  useEffect(
    () =>
      subscribeCookiePreferencesOpen(() => {
        setAnalyticsChecked(getCookieConsent()?.analytics ?? true);
        setConfiguring(true);
        setVisible(true);
      }),
    []
  );

  // Flag the document while the banner is up so surfaces that own a primary
  // action can reserve room for this fixed overlay (see `.consent-safe-bottom`
  // in index.css). Done imperatively on the root element rather than through a
  // provider, for the same reason this component sits outside the React tree:
  // consent state must never remount the app.
  useEffect(() => {
    const root = document.documentElement;
    if (visible) {
      root.dataset.cookieBanner = 'visible';
    } else {
      delete root.dataset.cookieBanner;
    }
    return () => {
      delete root.dataset.cookieBanner;
    };
  }, [visible]);

  const decide = (analytics: boolean) => {
    setCookieConsent(analytics);
    setVisible(false);
    setConfiguring(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.section
          {...slideUp}
          role="dialog"
          aria-label="Cookie consent"
          aria-live="polite"
          className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-3xl rounded-2xl border border-border-primary bg-surface-elevated p-4 shadow-2xl sm:p-5"
        >
          <div className="space-y-3">
            <p className="text-sm text-text-primary">
              We use essential cookies to keep you signed in and, with your permission,
              analytics cookies to understand how the platform is used. Essential cookies
              are always on. Read our{' '}
              <a
                href={LEGAL_DOCS.cookiesPolicy}
                target="_blank"
                rel="noreferrer noopener"
                className="underline underline-offset-2 transition-colors duration-fast hover:text-primary-600 dark:hover:text-primary-300 focus-ring rounded-sm"
              >
                Cookies Policy
              </a>
              .
            </p>

            {configuring && (
              <div className="space-y-2 rounded-xl border border-border-primary bg-surface-primary p-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="mt-0.5 h-4 w-4 accent-primary-500"
                    aria-label="Essential cookies (always active)"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-text-primary">Essential</span>
                    <span className="block text-text-secondary">
                      Sign-in and payment security. Always active.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={analyticsChecked}
                    onChange={(e) => setAnalyticsChecked(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-primary-500 focus-ring"
                    aria-label="Analytics cookies (PostHog)"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-text-primary">Analytics</span>
                    <span className="block text-text-secondary">
                      Helps us find errors and improve the platform (PostHog).
                    </span>
                  </span>
                </label>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-end gap-2">
              {configuring ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => decide(analyticsChecked)}
                >
                  Save preferences
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setConfiguring(true)}
                  >
                    Configure
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => decide(false)}
                  >
                    Reject non-essential
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => decide(true)}
                  >
                    Accept all
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
